import React, { useState, useEffect, useRef } from "react";
import { i18n } from "../../../../../../localization/i18n";
import { showToast } from "../../../../../../components/CustomToast";
import { IPersonFormData, ICheckoutData } from "../../dtos.d";
import { FiCheckCircle, FiShield, FiCreditCard, FiArrowLeft, FiCopy, FiTag, FiX } from "react-icons/fi";
import { FloatingSelect } from "../../../../../../components/FloatingSelect";
import { OutlinedInput } from "../../../../../../components/OutlinedInput";
import { SolidButton } from "../../../../../../components/SolidButton";
import { FaPix } from "react-icons/fa6";
import axios from "axios";
import api from "../../../../../../services/api";
import {
  Container,
  PixBox,
  QrCodeImage,
  CopyPasteContainer,
  CopyButton,
  PaymentArea,
  SectionTitle,
  CheckboxLabel,
  CheckoutCheckbox,
  SwitchContainer,
  SwitchButton,
  BillingAddressContainer,
  Row,
  FlexInput,
  PaymentMethodGrid,
  PaymentCard,
  PlanTitle,
  Divider,
  OverlayLoader,
  CouponContainer,
  TotalValueBar,
  SecurityBadge,
  Footer,
  Spinner,
  LeftPanel,
  StepTitle,
  SectionContainer,
  CardDataContainer,
  BackButton
} from "./styles";

interface StepCheckoutProps {
  courseId: string | undefined;
  courseData: { name: string; price: number } | null;
  personFormData: IPersonFormData | undefined;
  checkoutData: ICheckoutData | undefined;
  setCheckoutData: any;
  nextStep: () => void;
  prevStep: () => void;
  loading: boolean;
  setLoading: any;
  transactionCode: string;
  setTransactionCode: (code: string) => void;
  paymentLink: string;
  setPaymentLink: (link: string) => void;
}

export function StepCheckout({
  courseId,
  courseData,
  personFormData,
  checkoutData,
  setCheckoutData,
  nextStep,
  prevStep,
  loading,
  setLoading,
  transactionCode,
  setTransactionCode,
  paymentLink,
  setPaymentLink
}: StepCheckoutProps) {
  
  const [cardNumber, setCardNumber] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardCvv, setCardCvv] = useState("");
  const [cardHolder, setCardHolder] = useState("");
  const [searchingCep, setSearchingCep] = useState(false);
  const [pixGenerated, setPixGenerated] = useState(false);
  const [pixQrCode, setPixQrCode] = useState("");
  const [pixCopyPaste, setPixCopyPaste] = useState("");
  const [isPollingPix, setIsPollingPix] = useState(false);
  const [cardInAnalysis, setCardInAnalysis] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState<{ id?: string, code: string, type: "percent" | "fixed", value: number } | null>(null);

  // Referência para podermos limpar o intervalo caso o componente desmonte
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Polling para PIX ou para Cartão de Crédito em análise (PENDING)
    if ((pixGenerated && checkoutData?.paymentPlan === "pix" && transactionCode) || 
        (cardInAnalysis && checkoutData?.paymentPlan === "credit_card" && transactionCode)) {
      
      setIsPollingPix(true);
      
      intervalRef.current = setInterval(async () => {
        try {
          const response = await api.get(`/fixiters/payment-status/${transactionCode}`);
          
          if (response.data.status === "PAID" || response.data.status === "CONFIRMED" || response.data.status === "RECEIVED") {
            if (intervalRef.current) clearInterval(intervalRef.current);
            setIsPollingPix(false);
            setCardInAnalysis(false);
            showToast({ type: "success", message: "Pagamento confirmado! Avançando..." });
            nextStep();
          }
        } catch (err) {
          console.log("Aguardando confirmação de pagamento...");
        }
      }, 5000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        setIsPollingPix(false);
      }
    };
  }, [pixGenerated, cardInAnalysis, transactionCode, checkoutData?.paymentPlan, nextStep]);

  const cepMask = (value: string) => {
    return value
      .replace(/\D/g, "")
      .replace(/(\d{5})(\d)/, "$1-$2")
      .substring(0, 9);
  };

  const handleCepChange = async (value: string) => {
    const maskedCep = cepMask(value);
    setCheckoutData({ ...checkoutData, billingCep: maskedCep });

    const cleanCep = maskedCep.replace(/\D/g, "");
    if (cleanCep.length === 8) {
      setSearchingCep(true);
      try {
        const response = await axios.get(`https://viacep.com.br/ws/${cleanCep}/json/`);
        if (response.data && !response.data.erro) {
          setCheckoutData((prev: any) => ({
            ...prev,
            billingAddress: response.data.logradouro,
            billingDistrict: response.data.bairro,
            billingCity: response.data.localidade,
            billingState: response.data.uf,
          }));
        } else {
          showToast({ type: "error", message: "CEP não encontrado." });
        }
      } catch (err) {
        showToast({ type: "error", message: "Erro ao buscar CEP." });
      } finally {
        setSearchingCep(false);
      }
    }
  };

  const handleCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || "";
    let parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    if (parts.length) {
      setCardNumber(parts.join(" "));
    } else {
      setCardNumber(value);
    }
  };

  const handleExpiry = (value: string) => {
    let v = value.replace(/\D/g, "");
    if (v.length > 2) {
      v = v.substring(0, 2) + "/" + v.substring(2, 4);
    }
    setCardExpiry(v);
  };

  const handleCvv = (value: string) => {
    setCardCvv(value.replace(/\D/g, "").substring(0, 4));
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      showToast({ type: "error", message: "Digite um código de cupom válido." });
      return;
    }

    setValidatingCoupon(true);
    try {
      const response = await api.get(`/coupons/validate/${couponCode.trim()}`);
      // Assuming response.data returns { id?: string, type: 'percent' | 'fixed', value: number }
      if (response.data) {
        setAppliedCoupon({
          id: response.data.id,
          code: couponCode.trim(),
          type: response.data.type || "percent",
          value: Number(response.data.value || 0)
        });
        showToast({ type: "success", message: "Cupom aplicado com sucesso!" });
      }
    } catch (err: any) {
      setAppliedCoupon(null);
      const errorMsg = err.response?.data?.message || "Cupom inválido ou expirado.";
      showToast({ type: "error", message: errorMsg });
    } finally {
      setValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    showToast({ type: "success", message: "Cupom removido." });
  };

  const baseValue = Number(courseData?.price || 0);
  const heatGunDiscount = personFormData?.hasHeatGun === false ? 150 : 0;
  
  let subtotal = baseValue - heatGunDiscount;
  let couponDiscountAmount = 0;
  
  if (appliedCoupon) {
    if (appliedCoupon.type === "percent") {
      couponDiscountAmount = subtotal * (appliedCoupon.value / 100);
    } else if (appliedCoupon.type === "fixed") {
      couponDiscountAmount = appliedCoupon.value;
    }
    subtotal -= couponDiscountAmount;
  }

  if (subtotal < 0) {
    couponDiscountAmount += subtotal; // Adjust if it went below 0
    subtotal = 0; // Prevent negative totals
  }

  const creditTotal = subtotal;
  const pixDiscountAmount = creditTotal * 0.07;
  const pixTotal = creditTotal - pixDiscountAmount; // 7% discount

  const handleSelectPlan = (plan: "credit_card" | "pix", value: number) => {
    setCheckoutData({
      ...checkoutData,
      paymentPlan: plan,
      totalValue: value
    });
  };

  const handleCheckout = async () => {
    if (!checkoutData?.paymentPlan) {
      showToast({ type: "error", message: "Por favor, selecione uma forma de pagamento." });
      return;
    }

    if (checkoutData?.useStudentData === false) {
      if (!checkoutData.billingName) {
        showToast({ type: "error", message: "Informe o Nome/Razão Social do faturamento." });
        return;
      }
      if (!checkoutData.billingDocument || checkoutData.billingDocument.length < 14) {
        showToast({ type: "error", message: "Documento de faturamento inválido." });
        return;
      }
    }

    if (checkoutData?.useDeliveryAddressForBilling === false) {
      if (!checkoutData.billingCep || checkoutData.billingCep.length < 9) {
        showToast({ type: "error", message: "CEP de faturamento inválido." });
        return;
      }
      if (!checkoutData.billingAddress || !checkoutData.billingDistrict || !checkoutData.billingCity || !checkoutData.billingState) {
        showToast({ type: "error", message: "Preencha todos os campos do endereço de faturamento." });
        return;
      }
      if (!checkoutData.billingNumber && !checkoutData.billingNoNumber) {
        showToast({ type: "error", message: "Informe o número do faturamento ou marque 'Não possui número'." });
        return;
      }
    }

    if (checkoutData?.paymentPlan === "credit_card") {
      if (cardNumber.length < 18) {
        showToast({ type: "error", message: "Cartão de crédito inválido." });
        return;
      }
      if (!cardHolder || !cardExpiry || !cardCvv) {
        showToast({ type: "error", message: "Preencha todos os dados do cartão." });
        return;
      }
    }
    
    showToast({ type: "success", message: i18n.t("fixiterRegistration.step2.processing") as string });
    setLoading(true);
    
    try {
      const isPix = checkoutData?.paymentPlan === "pix";
      
      const payload: any = {
        course_id: courseId,
        ...(appliedCoupon?.code ? { coupon_code: appliedCoupon.code } : {}),

        metadata: {
          wants_heat_gun: personFormData?.hasHeatGun || false,
          ...(personFormData?.hasHeatGun && personFormData?.heatGunVoltage ? { heat_gun_voltage: personFormData.heatGunVoltage } : {})
        },

        personal: {
          name: personFormData?.name,
          cpf: personFormData?.cpf?.replace(/\D/g, ""),
          email: personFormData?.email,
          cellphone: personFormData?.cellphone?.replace(/\D/g, ""),
          expertise: personFormData?.expertise,
          professional_number: personFormData?.professional_number,
          country: "BRA",
          state: personFormData?.state,
          city: personFormData?.city
        },
        billing_info: {
          is_different: !checkoutData?.useStudentData,
          person_type: checkoutData?.billingType,
          name: checkoutData?.billingName,
          document: checkoutData?.billingDocument?.replace(/\D/g, ""),
          address: {
            cep: checkoutData?.useDeliveryAddressForBilling ? personFormData?.cep?.replace(/\D/g, "") : checkoutData?.billingCep?.replace(/\D/g, ""),
            address: checkoutData?.useDeliveryAddressForBilling ? personFormData?.address : checkoutData?.billingAddress,
            number: checkoutData?.useDeliveryAddressForBilling 
              ? (personFormData?.noNumber ? "S/N" : personFormData?.number) 
              : (checkoutData?.billingNoNumber ? "S/N" : checkoutData?.billingNumber),
            complement: checkoutData?.useDeliveryAddressForBilling
              ? (personFormData?.noComplement ? "" : personFormData?.complement)
              : (checkoutData?.billingNoComplement ? "" : checkoutData?.billingComplement),
            district: checkoutData?.useDeliveryAddressForBilling ? personFormData?.district : checkoutData?.billingDistrict,
            city: checkoutData?.useDeliveryAddressForBilling ? personFormData?.city : checkoutData?.billingCity,
            state: checkoutData?.useDeliveryAddressForBilling ? personFormData?.state : checkoutData?.billingState,
            country: "BRA"
          }
        },
        address: {
          cep: personFormData?.cep?.replace(/\D/g, ""),
          address: personFormData?.address,
          number: personFormData?.noNumber ? "S/N" : personFormData?.number,
          complement: personFormData?.noComplement ? "" : personFormData?.complement,
          district: personFormData?.district,
          city: personFormData?.city,
          state: personFormData?.state,
          country: "BRA"
        },
        payment: {
          method: isPix ? "PIX" : "CREDIT_CARD"
        }
      };

      if (!isPix) {
        payload.payment.installmentCount = checkoutData?.installments || 1;

        const [expMonth, expYearPrefix] = cardExpiry.split("/");
        const expYear = expYearPrefix.length === 2 ? `20${expYearPrefix}` : expYearPrefix;

        payload.payment.credit_card = {
          holderName: cardHolder,
          number: cardNumber.replace(/\D/g, ""),
          expiryMonth: expMonth,
          expiryYear: expYear,
          ccv: cardCvv
        };

        const document = checkoutData?.useStudentData ? personFormData?.cpf?.replace(/\D/g, "") : checkoutData?.billingDocument?.replace(/\D/g, "");
        const addressNum = checkoutData?.useDeliveryAddressForBilling
              ? (personFormData?.noNumber ? "S/N" : personFormData?.number) 
              : (checkoutData?.billingNoNumber ? "S/N" : checkoutData?.billingNumber);
        
        const postalCodeStr = checkoutData?.useDeliveryAddressForBilling ? personFormData?.cep?.replace(/\D/g, "") : checkoutData?.billingCep?.replace(/\D/g, "");
        
        payload.payment.credit_card_holder_info = {
          name: cardHolder,
          email: personFormData?.email,
          cpfCnpj: document,
          postalCode: postalCodeStr,
          addressNumber: addressNum,
          addressComplement: "",
          phone: personFormData?.cellphone?.replace(/\D/g, "")
        };
      }

      const response = await api.post("/fixiters/register", payload);

      const { transaction_code, payment_link, pix_qr_code, pix_copy_paste, status } = response.data;
      
      setTransactionCode(transaction_code);

      if (isPix) {
        if (!pix_qr_code || !pix_copy_paste) {
          throw new Error(`Dados do PIX incompletos no retorno. pix_qr_code recebido: ${typeof pix_qr_code}, pix_copy_paste recebido: ${typeof pix_copy_paste}. Resposta completa: ${JSON.stringify(response.data)}`);
        }
        setPaymentLink(payment_link || "");
        setPixQrCode(pix_qr_code);
        setPixCopyPaste(pix_copy_paste);
        setPixGenerated(true);
      } else {
        if (status === "PENDING") {
          // Cenário B: Análise antifraude
          setCardInAnalysis(true);
        } else if (status === "CONFIRMED" || status === "RECEIVED" || status === "PAID") {
          // Cenário A: Aprovado Imediatamente
          showToast({ type: "success", message: "Pagamento aprovado!" });
          nextStep();
        } else {
          // Status inesperado ou recusado que não gerou throw 400
          showToast({ type: "error", message: `Falha na transação. Status retornado: ${status}` });
        }
      }
    } catch (err: any) {
      console.error("Erro no handleCheckout:", err);
      
      let errorMsg = "Erro ao processar pagamento.";
      const responseData = err?.response?.data;

      if (responseData) {
        if (responseData.errors && Array.isArray(responseData.errors) && responseData.errors.length > 0) {
          // Fallback caso o backend repasse o Asaas cru
          errorMsg = responseData.errors[0].description || responseData.errors[0].message || errorMsg;
        } else if (responseData.message) {
          // O formato atual do seu backend: captura a chave 'message'
          errorMsg = responseData.message;
        } else if (typeof responseData === 'string') {
          errorMsg = responseData;
        }
      } else if (err?.message) {
        errorMsg = err.message;
      }

      setCardInAnalysis(false); // Reseta a tela de análise caso tenha travado visualmente
      showToast({ type: "error", message: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      {loading && (
        <OverlayLoader>
          <Spinner />
          <p>Processando pagamento...</p>
        </OverlayLoader>
      )}
      <LeftPanel>
        {!pixGenerated && (
          <>
            <StepTitle>{i18n.t("fixiterRegistration.step2.title") as string}</StepTitle>

            <SectionContainer>
          <SectionTitle>Dados de Faturamento (Nota Fiscal)</SectionTitle>
          <CheckboxLabel style={{ marginTop: "16px" }}>
            <CheckoutCheckbox 
              type="checkbox" 
              checked={checkoutData?.useStudentData !== false}
              onChange={(e) => setCheckoutData({ ...checkoutData, useStudentData: e.target.checked })}
            />
            Usar os mesmos dados do cadastro (Nome e CPF)
          </CheckboxLabel>

          {checkoutData?.useStudentData === false && (
            <BillingAddressContainer>
              
              <SwitchContainer>
                <SwitchButton
                  type="button"
                  active={checkoutData?.billingType === "FISICA" || !checkoutData?.billingType}
                  onClick={() => setCheckoutData({ ...checkoutData, billingType: "FISICA", billingDocument: "" })}
                >
                  Pessoa Física
                </SwitchButton>
                <SwitchButton
                  type="button"
                  active={checkoutData?.billingType === "JURIDICA"}
                  onClick={() => setCheckoutData({ ...checkoutData, billingType: "JURIDICA", billingDocument: "" })}
                >
                  Pessoa Jurídica
                </SwitchButton>
              </SwitchContainer>

              <OutlinedInput 
                inputName="billingName"
                label={checkoutData?.billingType === "JURIDICA" ? "Razão Social" : "Nome Completo do Titular"}
                value={checkoutData?.billingName || ""}
                handleChange={(val) => setCheckoutData({ ...checkoutData, billingName: val })}
              />

              <OutlinedInput 
                inputName="billingDocument"
                label={checkoutData?.billingType === "JURIDICA" ? "CNPJ" : "CPF"}
                value={checkoutData?.billingDocument || ""}
                handleChange={(val) => {
                  let masked = val.replace(/\D/g, "");
                  if (checkoutData?.billingType === "JURIDICA") {
                    masked = masked.replace(/^(\d{2})(\d)/, "$1.$2").replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3").replace(/\.(\d{3})(\d)/, ".$1/$2").replace(/(\d{4})(\d)/, "$1-$2").substring(0, 18);
                  } else {
                    masked = masked.replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{1,2})/, "$1-$2").replace(/(-\d{2})\d+?$/, "$1").substring(0, 14);
                  }
                  setCheckoutData({ ...checkoutData, billingDocument: masked });
                }}
              />
            </BillingAddressContainer>
          )}

          <CheckboxLabel style={{ marginTop: "16px" }}>
            <CheckoutCheckbox 
              type="checkbox" 
              checked={checkoutData?.useDeliveryAddressForBilling !== false}
              onChange={(e) => setCheckoutData({ ...checkoutData, useDeliveryAddressForBilling: e.target.checked })}
            />
            Usar o mesmo endereço de entrega para a Nota Fiscal
          </CheckboxLabel>

          {checkoutData?.useDeliveryAddressForBilling === false && (
            <BillingAddressContainer>
              <Row>
                <FlexInput flex={1}>
                  <OutlinedInput
                    inputName="billingCep"
                    label={searchingCep ? "Buscando CEP..." : "CEP"}
                    handleChange={handleCepChange}
                    value={checkoutData.billingCep || ""}
                  />
                </FlexInput>
                <FlexInput flex={3}>
                  <OutlinedInput
                    inputName="billingAddress"
                    label="Rua / Logradouro"
                    handleChange={(val) => setCheckoutData({ ...checkoutData, billingAddress: val })}
                    value={checkoutData.billingAddress || ""}
                  />
                </FlexInput>
              </Row>

              <Row>
                <FlexInput>
                  <OutlinedInput
                    inputName="billingNumber"
                    label="Número"
                    handleChange={(val) => setCheckoutData({ ...checkoutData, billingNumber: val })}
                    value={checkoutData.billingNoNumber ? "S/N" : (checkoutData.billingNumber || "")}
                    disabled={checkoutData.billingNoNumber}
                  />
                  <CheckboxLabel>
                    <CheckoutCheckbox 
                      type="checkbox" 
                      checked={checkoutData.billingNoNumber}
                      onChange={(e) => {
                        setCheckoutData({ ...checkoutData, billingNoNumber: e.target.checked, billingNumber: e.target.checked ? "" : checkoutData.billingNumber });
                      }} 
                    />
                    Não possui número
                  </CheckboxLabel>
                </FlexInput>

                <FlexInput>
                  <OutlinedInput
                    inputName="billingComplement"
                    label="Complemento"
                    handleChange={(val) => setCheckoutData({ ...checkoutData, billingComplement: val })}
                    value={checkoutData.billingNoComplement ? "Não possui" : (checkoutData.billingComplement || "")}
                    disabled={checkoutData.billingNoComplement}
                  />
                  <CheckboxLabel>
                    <CheckoutCheckbox 
                      type="checkbox" 
                      checked={checkoutData.billingNoComplement}
                      onChange={(e) => {
                        setCheckoutData({ ...checkoutData, billingNoComplement: e.target.checked, billingComplement: e.target.checked ? "" : checkoutData.billingComplement });
                      }} 
                    />
                    Sem complemento
                  </CheckboxLabel>
                </FlexInput>
              </Row>

              <Row>
                <FlexInput flex={2}>
                  <OutlinedInput
                    inputName="billingDistrict"
                    label="Bairro"
                    handleChange={(val) => setCheckoutData({ ...checkoutData, billingDistrict: val })}
                    value={checkoutData.billingDistrict || ""}
                  />
                </FlexInput>
                <FlexInput flex={2}>
                  <OutlinedInput
                    inputName="billingCity"
                    label="Cidade"
                    handleChange={(val) => setCheckoutData({ ...checkoutData, billingCity: val })}
                    value={checkoutData.billingCity || ""}
                  />
                </FlexInput>
                <FlexInput flex={1}>
                  <OutlinedInput
                    inputName="billingState"
                    label="UF"
                    handleChange={(val) => setCheckoutData({ ...checkoutData, billingState: val })}
                    value={checkoutData.billingState || ""}
                  />
                </FlexInput>
              </Row>
            </BillingAddressContainer>
          )}
        </SectionContainer>

        <PaymentMethodGrid>
          <PaymentCard 
            selected={checkoutData?.paymentPlan === "credit_card"}
            onClick={() => handleSelectPlan("credit_card", creditTotal)}
          >
            <PlanTitle><FiCreditCard style={{ marginRight: '8px', verticalAlign: 'middle' }} />Cartão de crédito</PlanTitle>
          </PaymentCard>

          <PaymentCard 
            selected={checkoutData?.paymentPlan === "pix"}
            onClick={() => handleSelectPlan("pix", pixTotal)}
          >
            <PlanTitle><FaPix style={{ marginRight: '8px', verticalAlign: 'middle' }} />Pix</PlanTitle>
          </PaymentCard>
        </PaymentMethodGrid>

        {/* Dynamic Payment Details Area */}
        {checkoutData?.paymentPlan === "credit_card" && !cardInAnalysis && (
          <PaymentArea>
            <SectionTitle>Dados do Cartão</SectionTitle>
            
            <CardDataContainer>
              <OutlinedInput 
                inputName="cardHolder"
                label="Nome impresso no cartão"
                value={cardHolder}
                handleChange={(val) => setCardHolder(val.toUpperCase())}
              />

              <OutlinedInput 
                inputName="cardNumber"
                label="Número do Cartão"
                value={cardNumber}
                handleChange={handleCardNumber}
              />

              <Row>
                <FlexInput>
                  <OutlinedInput 
                    inputName="cardExpiry"
                    label="Data de Validade (MM/AA)"
                    value={cardExpiry}
                    handleChange={handleExpiry}
                  />
                </FlexInput>
                <FlexInput>
                  <OutlinedInput 
                    inputName="cardCvv"
                    label="Código de Segurança (CVV)"
                    value={cardCvv}
                    handleChange={handleCvv}
                  />
                </FlexInput>
              </Row>

              <Divider />

              <div style={{ marginTop: "16px" }}>
                <FloatingSelect
                  label="Número de parcelas"
                  value={checkoutData?.installments || ""}
                  onChange={(e) => setCheckoutData({ ...checkoutData, installments: parseInt(e.target.value) })}
                >
                  <option value="" disabled hidden></option>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(num => {
                    const parcelValue = (creditTotal / num).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    return <option key={num} value={num}>{num}x de R$ {parcelValue} sem juros</option>
                  })}
                </FloatingSelect>
              </div>
            </CardDataContainer>
          </PaymentArea>
        )}
        {checkoutData?.paymentPlan === "credit_card" && cardInAnalysis && (
          <PixBox>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "var(--fixit)", justifyContent: "center" }}>
              <FiShield size={24} />
              <h3 style={{ margin: 0, fontSize: "18px", color: "var(--black)" }}>Analisando Pagamento</h3>
            </div>
            
            <div style={{ display: "flex", justifyContent: "center", margin: "24px 0" }}>
              <Spinner style={{ width: "40px", height: "40px", borderTopColor: "var(--fixit)", borderRightColor: "var(--fixit)" }} />
            </div>

            <p style={{ margin: 0, fontSize: "14px", color: "var(--gray)", textAlign: "center", lineHeight: 1.5 }}>
              Seu pagamento está em análise pela operadora do cartão. <br/> Por favor, aguarde a confirmação...
            </p>
          </PixBox>
        )}
          </>
        )}

        {checkoutData?.paymentPlan === "pix" && pixGenerated && (
          <PixBox>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "var(--fixit)" }}>
              <FaPix size={24} />
              <h3 style={{ margin: 0, fontSize: "18px", color: "var(--black)" }}>Pagamento via Pix</h3>
            </div>
            <p style={{ margin: 0, fontSize: "14px", color: "var(--gray)", textAlign: "center", lineHeight: 1.5 }}>
              Escaneie o QR Code abaixo com o aplicativo do seu banco ou copie o código Pix.
            </p>
            
            <QrCodeImage 
              src={pixQrCode?.startsWith?.("data:image") ? pixQrCode : `data:image/png;base64,${pixQrCode || ""}`} 
              alt="QR Code Pix" 
            />
            
            <CopyPasteContainer>
              <div style={{ wordBreak: "break-all", fontSize: "12px", marginBottom: "8px" }}>
                {pixCopyPaste || "Código Copia e Cola não disponível"}
              </div>
              <CopyButton onClick={() => {
                navigator.clipboard.writeText(pixCopyPaste);
                showToast({ type: "success", message: "Código copiado!" });
              }}>
                <FiCopy size={16} /> Copiar
              </CopyButton>
            </CopyPasteContainer>

            {isPollingPix && (
              <div style={{
                marginTop: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                color: "var(--fixit)",
                fontWeight: "bold",
                fontSize: "14px",
              }}>
                <Spinner style={{ width: "16px", height: "16px", borderTopColor: "var(--fixit)", borderRightColor: "var(--fixit)" }} />
                Aguardando pagamento...
              </div>
            )}

            <button
              type="button"
              onClick={() => setPixGenerated(false)}
              disabled={loading}
              style={{
                marginTop: "24px",
                background: "transparent",
                border: "none",
                color: "var(--fixit)",
                textDecoration: "underline",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              <FiArrowLeft style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              Alterar forma de pagamento
            </button>
          </PixBox>
        )}
        
        {checkoutData?.paymentPlan && (!pixGenerated) && (!cardInAnalysis) && (
          <TotalValueBar>
            <CouponContainer>
              {!appliedCoupon ? (
                <div className="coupon-input-row">
                  <div style={{ flex: 1 }}>
                    <OutlinedInput 
                      inputName="couponCode"
                      label="Cupom de desconto"
                      value={couponCode}
                      handleChange={(v) => setCouponCode(v.toUpperCase())}
                    />
                  </div>
                  <SolidButton
                    text={validatingCoupon ? "..." : "Aplicar"}
                    onClick={handleApplyCoupon}
                    disabled={validatingCoupon || !couponCode.trim()}
                    buttonHeight={46}
                    style={{ width: "auto", minWidth: "100px" }}
                  />
                </div>
              ) : (
                <div className="coupon-applied">
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <FiTag />
                    <span>
                      Cupom <strong>{appliedCoupon.code}</strong> aplicado!
                      ({appliedCoupon.type === "percent" ? `-${appliedCoupon.value}%` : `-R$ ${appliedCoupon.value.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`})
                    </span>
                  </div>
                  <button onClick={removeCoupon} title="Remover cupom">
                    <FiX size={18} />
                  </button>
                </div>
              )}
            </CouponContainer>

            <div className="total-text" style={{ alignItems: "center" }}>
              <span>Total da compra:</span>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                
                {(checkoutData.paymentPlan === "pix" || heatGunDiscount > 0 || couponDiscountAmount > 0) && (
                  <span style={{ fontSize: "14px", color: "var(--gray)", textDecoration: "line-through", marginBottom: "2px" }}>
                    De R$ {baseValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                )}
                
                <span className="price">
                  R$ {(checkoutData.paymentPlan === "pix" ? pixTotal : creditTotal).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>

            {/* Discount Badges Row - Decoupled to prevent flex squishing on mobile */}
            {(heatGunDiscount > 0 || couponDiscountAmount > 0 || (checkoutData.paymentPlan === "pix" && pixDiscountAmount > 0)) && (
              <div style={{ 
                display: "flex", 
                gap: "8px", 
                flexWrap: "wrap", 
                justifyContent: "flex-end", 
                marginTop: "-16px", 
                marginBottom: "24px", 
                width: "100%", 
              }}>
                {heatGunDiscount > 0 && (
                  <span style={{ fontSize: "12px", color: "#4caf50", fontWeight: "bold", background: "#e8f5e9", padding: "4px 8px", borderRadius: "6px", textAlign: "right", whiteSpace: "nowrap" }}>
                    Sem soprador: -R$ 150,00
                  </span>
                )}
                
                {appliedCoupon && couponDiscountAmount > 0 && (
                  <span style={{ fontSize: "12px", color: "#4caf50", fontWeight: "bold", background: "#e8f5e9", padding: "4px 8px", borderRadius: "6px", textAlign: "right", whiteSpace: "nowrap" }}>
                    {appliedCoupon.type === "percent" ? `Cupom (${appliedCoupon.value}%): -R$ ${couponDiscountAmount.toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : `Cupom: -R$ ${couponDiscountAmount.toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`}
                  </span>
                )}
                  
                  {checkoutData.paymentPlan === "pix" && pixDiscountAmount > 0 && (
                    <span style={{ fontSize: "12px", color: "#4caf50", fontWeight: "bold", background: "#e8f5e9", padding: "4px 8px", borderRadius: "6px", textAlign: "right", whiteSpace: "nowrap" }}>
                      Pix (7%): -R$ {pixDiscountAmount.toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </span>
                  )}
                </div>
            )}
            
            <SolidButton
              text={checkoutData.paymentPlan === "pix" ? "Gerar código PIX" : "Confirmar Pagamento"}
              fullWidth
              buttonHeight={56}
              onClick={handleCheckout}
              loading={loading}
              disabled={loading}
              style={{ fontSize: "18px" }}
            />
            
            <SecurityBadge>
              <FiCheckCircle color="#4caf50" /> Compra 100% Segura. Proteção de dados garantida.
            </SecurityBadge>
          </TotalValueBar>
        )}
        {!pixGenerated && !cardInAnalysis && (
          <Footer>
            <BackButton onClick={prevStep}>
              <FiArrowLeft /> Voltar
            </BackButton>
          </Footer>
        )}
      </LeftPanel>
    </Container>
  );
}